import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { ActionMenuContainerProps, ActionMenuItemProps } from './action-menu.types';
import { css } from '@emotion/react';
import { Typography } from '@design-system/atoms/typography';

export const underlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 999;
`;

export const ActionMenuContainer = styled(motion.div)<ActionMenuContainerProps>`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  position: fixed;
  z-index: 10000;
  background-color: ${({ theme }) => theme.colors.technical['adaptive-reverse']};
  color: ${({ theme }) => theme.colors.technical.gray[60]};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 140px;

  ${({ hasTitle }) =>
    hasTitle &&
    css`
      padding: 12px;
    `}

  ${({ placement }) => {
    switch (placement) {
      case 'top':
        return `
          transform: translateX(-50%);
          transform-origin: bottom center;
        `;
      case 'bottom':
        return `
          transform: translateX(-50%);
          transform-origin: top center;
        `;
      case 'left':
        return `
          transform-origin: right center;
        `;
      case 'right':
      default:
        return `
          transform-origin: left center;
        `;
    }
  }}
`;

export const Item = styled.button<
  Pick<ActionMenuItemProps, 'isActive' | 'isDisabled' | 'isDanger'> & { isFocusVisible: boolean }
>`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  color: ${({ theme, isDanger }) => (isDanger ? theme.colors.status.error.normal : theme.colors.technical.gray[60])};
  background-color: transparent;

  ${({ isFocusVisible, theme }) =>
    isFocusVisible &&
    css`
      outline: 2px solid ${theme.colors.primary.normal};
      outline-offset: -2px;
    `};

  &:hover {
    color: ${({ theme, isDanger }) => (isDanger ? theme.colors.status.error.normal : theme.colors.primary.normal)};
    background-color: ${({ theme, isDanger }) =>
      isDanger ? theme.colors.status.error.highlight : theme.colors.secondary.normal};
  }

  &:active {
    color: ${({ theme, isDanger }) => (isDanger ? theme.colors.status.error.dark : theme.colors.primary.medium)};
  }

  ${({ isActive, theme }) =>
    isActive &&
    css`
      color: ${theme.colors.primary.normal};
      background-color: ${theme.colors.secondary.normal};
    `}

  ${({ isDisabled, theme }) =>
    isDisabled &&
    css`
      color: ${theme.colors.technical.gray[15]};
      background-color: transparent;
      pointer-events: none;
    `}
`;

export const ActionMenuHeader = styled(Typography)`
  color: ${({ theme }) => theme.colors.technical.adaptive};
  margin-bottom: 12px;
`;

export const triggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 12px;
  color: white;
  cursor: default;
  outline: none;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  &:active {
    background: rgba(0, 0, 0, 0.4);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.75);
    outline-offset: 2px;
  }
`;

export const popover = styled.div`
  padding: 4px;
  width: 224px;
  overflow: auto;
  border-radius: 6px;
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

export const menu = styled.div`
  outline: none;
`;

export const header = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid #e1e1e1;
`;

export const menuItem = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 6px;
  padding: 8px 12px;
  box-sizing: border-box;
  outline: none;
  cursor: default;
  color: #333;

  &:focus {
    background: #6b46c1;
    color: white;
  }

  &:hover {
    background: #f5f5f5;
  }
`;

export const separator = styled.div`
  height: 1px;
  margin: 4px 12px;
  background: #e1e1e1;
`;
